import type { ReactNode } from 'react';

export interface DragState<T = unknown> {
  /** Id assigned by useDraggable. */
  id: string;
  /** Semantic type used to match draggables to droppables. */
  type: string;
  /** User-supplied payload. */
  data: T;
  /** Preview node the overlay paints during the drag. */
  preview: ReactNode | null;
  /** Pointer position in viewport coords. */
  pointer: { x: number; y: number };
  /** Delta from the initial pointer, used by overlay for translate. */
  delta: { x: number; y: number };
  /** Currently-hovered droppable id (if any). */
  over: string | null;
}

type Listener<T> = (state: DragState<T> | null) => void;

/** Central broker for active drags. Consumers usually never touch this
 *  directly — `useDraggable` / `useDroppable` / `<DragOverlay>` subscribe
 *  to it under the hood. Implements a pointer-events-based drag source of
 *  truth that survives React re-renders. Spec 13 §2.3. */
export class DragManager<T = unknown> {
  private readonly listeners = new Set<Listener<T>>();
  private state: DragState<T> | null = null;
  private readonly droppables = new Map<
    string,
    { accepts: ReadonlyArray<string>; rect: () => DOMRect | null }
  >();

  subscribe(listener: Listener<T>): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getState(): DragState<T> | null {
    return this.state;
  }

  registerDroppable(
    id: string,
    accepts: ReadonlyArray<string>,
    rect: () => DOMRect | null,
  ): () => void {
    this.droppables.set(id, { accepts, rect });
    return () => {
      this.droppables.delete(id);
    };
  }

  private emit(): void {
    for (const listener of this.listeners) listener(this.state);
  }

  start(state: DragState<T>): void {
    this.state = state;
    this.emit();
  }

  move(pointer: { x: number; y: number }): void {
    if (this.state === null) return;
    const start = this.state.pointer;
    const delta = {
      x: pointer.x - start.x + this.state.delta.x,
      y: pointer.y - start.y + this.state.delta.y,
    };
    // Detect the hovered droppable by hit-testing the registered rects.
    let over: string | null = null;
    for (const [id, cfg] of this.droppables) {
      if (cfg.accepts.length > 0 && !cfg.accepts.includes(this.state.type)) continue;
      const r = cfg.rect();
      if (r === null) continue;
      if (
        pointer.x >= r.left &&
        pointer.x <= r.right &&
        pointer.y >= r.top &&
        pointer.y <= r.bottom
      ) {
        over = id;
      }
    }
    this.state = { ...this.state, pointer, delta, over };
    this.emit();
  }

  end(): string | null {
    const over = this.state?.over ?? null;
    this.state = null;
    this.emit();
    return over;
  }

  cancel(): void {
    this.state = null;
    this.emit();
  }
}

/** Default manager for the module-level hooks. Consumers rarely need a
 *  second one — a single drag is active at a time per document. */
export const defaultDragManager: DragManager = new DragManager();
